// @flow

import React, { type Node } from 'react';
import { useUserInputAsTextField, useUserInputValue } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';

import { ErrorLabel } from '../../../../molecules/error-label';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useOpenedIncomingPayment } from '@tectonics/prs/incoming-payment';
import { getSafeEWalletPaymentOption } from '@tectonics/prs/incoming-payment/payment-option';
import { RadioButton } from '@tectonics/look-and-feel/shopify/commons/atoms';
import styled, { css } from 'styled-components';
import { getBankListImageSrc } from './get-bank-list-image-src';
import { getSafePaymentOptionsV2 } from '@tectonics/prs/incoming-payment/get-payment-options';

export function FieldNigerianBankList(): Node {
  const [value, handleChange] = useUserInputAsTextField('selectedBankCode');
  const [selectedPaymentMethod] = useUserInputValue('selectedPaymentMethod');

  const validationError = useUserInputValidationError('selectedBankCode');
  const formHasBeenSent = useFormHasBeenSent();

  const incomingPayment = useOpenedIncomingPayment();

  if (!selectedPaymentMethod) {
    return null;
  }

  const paymentOptions = getSafePaymentOptionsV2(incomingPayment);
  const bankList = getSafeEWalletPaymentOption(paymentOptions);

  return (
    <div data-test-name="customer-bank" style={{ gridColumn: '1/3' }}>
      <BankListGroup name="customer-bank" data-value={value} id="customer-bank">
        {bankList.map(({ name, value }) => (
          <BankListItem htmlFor={value} key={value} data-test-value={value}>
            <RadioButton
              id={value}
              value={value}
              onChange={handleChange}
              data-error={formHasBeenSent && validationError ? 'error' : null}
              name="customer-bank-radio"
            />

            <BankNameWrapper>
              {name}
            </BankNameWrapper>

            <BankIcon src={getBankListImageSrc(value)} alt={name} />
          </BankListItem>
        ))}
      </BankListGroup>

      {formHasBeenSent && validationError && (
        <ValidationMessage validationError={validationError} />
      )}
    </div>
  );
}

type ValidationMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationMessage({ validationError }: ValidationMessageProps): Node {
  switch (validationError.id) {
    case 'server-error':
      return (
        <ErrorLabel>
          {serverErrorMessage(validationError.payload)}
        </ErrorLabel>
      );

    default:
      return (
        <ErrorLabel>
          Invalid bank
        </ErrorLabel>
      );
  }
}

const BankListGroup = styled.div`
  border-radius: 5px;
  box-shadow: 0 0 0 1px rgba(63,63,68,0.05), 0 1px 3px 0 rgba(63,63,68,0.15);
  ${props => props.focused && css`
    box-shadow: 0 0 3px 1px ${props => props.theme.colors.primary(50)};
  `}
  color: ${props => props.theme.colors.primary(10)};
  max-height: 200px;
  overflow-x: hidden;
  overflow-y: scroll;
`;

const BankListItem = styled.label`
  position: relative;
  display: flex;
  cursor: pointer;
  padding: 16px;
  align-items: center;

  &:first-child {
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
  }

  &:last-child {
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
  }

  ${props => props.selected && css`
    background: ${props => props.theme.colors.gray(96)};
  `}
`;

const BankNameWrapper = styled.span`
  margin-left: 12px;
  font-size: 0.9em;
  font-weight: 600;
  flex-grow: 1;
  color: ${props => props.theme.colors.secondary(30)};
`;

const BankIcon = styled.img`
  height: 20px;
  width: auto;
  margin-left: auto;
`;
